import { useEffect, useMemo, useRef, useState } from 'react'
import { ChevronDown } from 'lucide-react'

import { cn } from '@/lib/utils'

export interface MultiOption {
  value: string
  label: string
  /** Shown muted after the label — an employee id, a node type. */
  hint?: string
}

/**
 * A filter over a long list (branches, people): a button that says what is
 * chosen, opening a searchable checklist. Nothing chosen means "All", as on
 * the server.
 */
export function MultiSelect({
  id,
  options,
  value,
  onChange,
  allLabel = 'All',
}: {
  id: string
  options: MultiOption[]
  value: string[]
  onChange: (next: string[]) => void
  allLabel?: string
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return options
    return options.filter((o) => `${o.label} ${o.hint ?? ''}`.toLowerCase().includes(q))
  }, [options, query])

  const chosen = options.filter((o) => value.includes(o.value))
  const summary =
    chosen.length === 0 ? allLabel : chosen.length <= 2 ? chosen.map((o) => o.label).join(', ') : `${chosen.length} selected`

  function toggle(v: string) {
    onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v])
  }

  return (
    <div ref={ref} className="relative">
      <button
        id={id}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-2 rounded-md border border-slate-200 bg-white px-2 py-1.5 text-left text-sm text-navy-900 hover:border-navy-200"
      >
        <span className="truncate">{summary}</span>
        <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" />
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-72 rounded-md border border-slate-200 bg-white p-2 shadow-lift">
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search"
            className="mb-2 w-full rounded-md border border-slate-200 px-2 py-1 text-sm"
          />
          <ul role="listbox" aria-multiselectable className="max-h-64 overflow-y-auto">
            {shown.length === 0 && <li className="px-2 py-1.5 text-sm text-slate-500">Nothing matches.</li>}
            {shown.map((o) => (
              <li key={o.value}>
                <label className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm hover:bg-navy-50">
                  <input type="checkbox" checked={value.includes(o.value)} onChange={() => toggle(o.value)} />
                  <span className="flex-1 truncate">{o.label}</span>
                  {o.hint && <span className="text-2xs text-slate-500">{o.hint}</span>}
                </label>
              </li>
            ))}
          </ul>
          <div className="mt-2 flex justify-between border-t border-slate-100 pt-2">
            <button
              type="button"
              onClick={() => onChange([])}
              className={cn('text-2xs font-semibold uppercase tracking-wider text-navy-700', value.length === 0 && 'invisible')}
            >
              Clear
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-2xs font-semibold uppercase tracking-wider text-navy-700"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
